import { useEffect, useRef, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Plus, Upload, ClipboardEdit, Send, X, Trash2, ExternalLink, ClipboardList, Check, Clock, Sparkles, Wand2, AlertCircle, Loader2, Eye, Lock, ShieldCheck, History } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";
import { MediaIframe, mediaSrc } from "@/lib/mediaSrc";
import { AnkleResponseView } from "@/components/ankle/AnkleFinderPanel";
import { FormSignOffPanel } from "@/components/FormSignOffPanel";

export default function ClinicianForms() {
  const { t } = useTranslation();
  const location = useLocation();
  const initialAssignmentId = useMemo(() => new URLSearchParams(location.search).get("assignment"), [location.search]);
  const [forms, setForms] = useState([]);
  const [tab, setTab] = useState(initialAssignmentId ? "assigned" : "library");
  const [patients, setPatients] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [show, setShow] = useState(false);
  const [kind, setKind] = useState("structured");
  const [form, setForm] = useState({ title: "", category: "Subjective assessment", description: "", fields: [] });
  const [assign, setAssign] = useState(null);
  const [pickPatient, setPickPatient] = useState("");
  const [removing, setRemoving] = useState("");
  const [deleting, setDeleting] = useState("");
  const [uploading, setUploading] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  const [uploadPending, setUploadPending] = useState(null);
  // Response viewer for completed assignments — shows digital answers or
  // the annotated PDF depending on how the patient submitted.
  const [viewingResp, setViewingResp] = useState(null);         // { assignment, response, patient, form }
  const [respLoading, setRespLoading] = useState(false);
  // Phase 1E Gap 2 · typed-name sign-off / lock / append-only amendments.
  const [signName, setSignName] = useState("");
  const [signing, setSigning] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amend, setAmend] = useState({ reason: "", note: "", name: "" });
  const [amending, setAmending] = useState(false);
  const respDialogRef = useModalA11y({ open: !!viewingResp, onClose: () => setViewingResp(null) });
  const openedFromLinkRef = useRef(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    const [f, p, a] = await Promise.all([
      api.get("/forms"),
      api.get("/patients"),
      api.get("/form-assignments/all").catch(() => ({ data: [] })),
    ]);
    setForms(f.data); setPatients(p.data); setAssignments(a.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const openResponse = async (a) => {
    setRespLoading(true);
    setViewingResp({ assignment: a });
    setSignName(""); setAmendOpen(false); setAmend({ reason: "", note: "", name: "" });
    try {
      const r = await api.get(`/form-responses/assignment/${a.id}`);
      const patient = patients.find((p) => p.id === a.patient_id) || null;
      const formDoc = forms.find((f) => f.id === a.form_id) || null;
      setViewingResp({ assignment: a, response: r.data, patient, form: formDoc });
    } catch (e) {
      toast.error("No response found for this assignment yet");
      setViewingResp(null);
    } finally { setRespLoading(false); }
  };

  const signLock = async () => {
    const r = viewingResp?.response;
    if (!r?.id) return;
    if (!signName.trim()) return toast.error("Type your name to sign & lock");
    setSigning(true);
    try {
      const { data } = await api.post(`/form-responses/${r.id}/sign`, { signed_name: signName.trim() });
      setViewingResp((s) => ({ ...s, response: data }));
      setSignName("");
      toast.success("Response signed & locked");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign & lock"); }
    finally { setSigning(false); }
  };

  const addAmendment = async () => {
    const r = viewingResp?.response;
    if (!r?.id) return;
    if (!amend.reason.trim()) return toast.error("Amendment reason is required");
    if (!amend.name.trim()) return toast.error("Type your name to record the amendment");
    setAmending(true);
    try {
      const { data } = await api.post(`/form-responses/${r.id}/amend`, {
        reason: amend.reason.trim(), note: amend.note.trim(), signed_name: amend.name.trim(),
      });
      setViewingResp((s) => ({ ...s, response: data }));
      setAmend({ reason: "", note: "", name: "" });
      setAmendOpen(false);
      toast.success("Amendment recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record amendment"); }
    finally { setAmending(false); }
  };

  // If we arrived from a notification `?assignment=<id>`, wait for
  // assignments to load then auto-open the response viewer.
  useEffect(() => {
    if (!initialAssignmentId || openedFromLinkRef.current || !assignments.length) return;
    const a = assignments.find((x) => x.id === initialAssignmentId);
    if (a) {
      openedFromLinkRef.current = true;
      setTab("assigned");
      if (a.status === "completed") openResponse(a);
    }
    // eslint-disable-next-line
  }, [initialAssignmentId, assignments]);

  const addField = () => setForm({ ...form, fields: [...form.fields, { key: `field_${form.fields.length + 1}`, label: "", type: "text", required: false }] });
  const updField = (i, patch) => { const nf = [...form.fields]; nf[i] = { ...nf[i], ...patch }; setForm({ ...form, fields: nf }); };
  const rmField = (i) => setForm({ ...form, fields: form.fields.filter((_, x) => x !== i) });

  const saveStructured = async (e) => {
    e.preventDefault();
    if (!form.title || form.fields.length === 0) return toast.error("Add a title and at least one field");
    try {
      await api.post("/forms", { ...form, kind: "structured" });
      toast.success("Form saved");
      setForm({ title: "", category: "Subjective assessment", description: "", fields: [] });
      setShow(false); refresh();
    } catch { toast.error("Failed"); }
  };

  // Step 1 of upload: user picked a file — open a title modal.
  const pickFile = (file) => {
    if (!file) return;
    setUploadPending({ file, title: file.name.replace(/\.pdf$/i, "") });
  };

  // Step 2: user confirmed a title — actually upload.
  const uploadPdf = async () => {
    if (!uploadPending?.file) return;
    const { file, title: rawTitle } = uploadPending;
    const title = (rawTitle || "").trim() || file.name.replace(/\.pdf$/i, "");
    const fd = new FormData();
    fd.append("file", file);
    setUploadPending(null);
    setUploading(true);
    try {
      const { data } = await api.post(
        `/forms/upload-pdf?title=${encodeURIComponent(title)}&category=${encodeURIComponent("Subjective assessment")}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 },
      );
      if (data?.digital_ready) {
        toast.success(`PDF uploaded and digitalised — ${data.parsed_fields?.length || 0} fields detected. Review before assigning.`);
        // Auto-open the review modal so the clinician can verify.
        setReviewing({ ...data, _draft: (data.parsed_fields || []).map((f) => ({ ...f })) });
      } else if (data?.parse_error) {
        toast.warning(`Uploaded — but AI parsing failed. ${data.parse_error}`);
      } else {
        toast.success("PDF form uploaded");
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openReview = (form) => {
    setReviewing({
      ...form,
      _draft: (form.parsed_fields && form.parsed_fields.length ? form.parsed_fields : form.fields || []).map((f) => ({ ...f })),
    });
  };

  const rvUpdate = (i, patch) => {
    setReviewing((s) => {
      const draft = [...s._draft];
      draft[i] = { ...draft[i], ...patch };
      return { ...s, _draft: draft };
    });
  };
  const rvAdd = () => setReviewing((s) => ({
    ...s,
    _draft: [...s._draft, { key: `field_${s._draft.length + 1}`, label: "", type: "text", required: false }],
  }));
  const rvRemove = (i) => setReviewing((s) => ({ ...s, _draft: s._draft.filter((_, x) => x !== i) }));

  const saveReview = async () => {
    if (!reviewing) return;
    // Client-side guard — must have at least 1 field with a label
    const cleaned = reviewing._draft.filter((f) => (f.label || "").trim());
    if (cleaned.length === 0) return toast.error("Add at least one labelled field before saving");
    setSavingReview(true);
    try {
      await api.patch(`/forms/${reviewing.id}`, { parsed_fields: cleaned });
      toast.success("Digital version saved — patients will now see an interactive form.");
      setReviewing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingReview(false);
    }
  };

  const doAssign = async () => {
    if (!pickPatient) return toast.error("Select a patient");
    try {
      await api.post("/form-assignments", { form_id: assign.id, patient_id: pickPatient });
      toast.success(`Assigned to patient — they'll see it in "Complete before appointment"`);
      setAssign(null); setPickPatient("");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const removeAssignment = async (a) => {
    if (!window.confirm(`Remove "${a.form_title}" from ${a.patient_name || "this patient"}?`)) return;
    setRemoving(a.id);
    try {
      await api.delete(`/form-assignments/${a.id}`);
      toast.success("Assignment removed");
      setAssignments((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove assignment");
    } finally {
      setRemoving("");
    }
  };

  const removeForm = async (f) => {
    if (!window.confirm(
      `Delete the form "${f.title}"?\n\n` +
      `This removes the template and any pending assignments. ` +
      `Completed patient responses (if any) will block deletion.`
    )) return;
    setDeleting(f.id);
    try {
      await api.delete(`/forms/${f.id}`);
      toast.success("Form deleted");
      setForms((prev) => prev.filter((x) => x.id !== f.id));
      // Any pending assignments referencing this form are gone too.
      setAssignments((prev) => prev.filter((x) => x.form_id !== f.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete form");
    } finally {
      setDeleting("");
    }
  };

  // SEC-003 · Preview links open in a new tab; fetch a scoped media token
  // just-in-time so a full JWT never leaks into URL history.
  const openFormPdf = async (id) => {
    try {
      const src = await mediaSrc("form", id);
      window.open(src, "_blank", "noopener,noreferrer");
    } catch { toast.error("Could not open PDF"); }
  };

  const closeReview = () => { if (savingReview) return; setReviewing(null); };
  const closeAssign = () => setAssign(null);
  const closeUploadModal = () => { if (uploading) return; setUploadPending(null); };
  const reviewRef = useModalA11y({ open: !!reviewing, onClose: closeReview });
  const assignRef = useModalA11y({ open: !!assign, onClose: closeAssign });
  const uploadTitleRef = useModalA11y({ open: !!uploadPending, onClose: closeUploadModal });

  return (
    <DashLayout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.cForms.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">{forms.length} forms</h1>
        </div>
        <div className="flex gap-2">
          <button
            className="fl-btn-ghost text-xs"
            onClick={() => fileRef.current?.click()}
            data-testid="form-upload-pdf"
            disabled={uploading}
          >
            {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12} />}
            {uploading ? "Digitalising…" : "Upload PDF"}
          </button>
          <input type="file" ref={fileRef} accept="application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
          <button className="fl-btn text-xs" onClick={() => { setShow(!show); setKind("structured"); }} data-testid="form-new-structured"><Plus size={12} /> New structured</button>
        </div>
      </div>

      {show && (
        <form onSubmit={saveStructured} className="fl-card p-6 mb-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <input required placeholder="Title" className="fl-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="form-title" />
            <input placeholder="Category" className="fl-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <input placeholder="Description" className="fl-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="fl-label">Fields ({form.fields.length})</div>
              <button type="button" className="fl-btn-ghost text-xs" onClick={addField} data-testid="form-add-field"><Plus size={12} /> Add field</button>
            </div>
            <div className="space-y-2">
              {form.fields.map((fld, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`form-field-${i}`}>
                  <input className="fl-input col-span-2 text-xs" placeholder="key" value={fld.key} onChange={(e) => updField(i, { key: e.target.value })} />
                  <input className="fl-input col-span-4 text-xs" placeholder="Label" value={fld.label} onChange={(e) => updField(i, { label: e.target.value })} />
                  <select className="fl-input col-span-2 text-xs" value={fld.type} onChange={(e) => updField(i, { type: e.target.value })}>
                    <option value="text">Text</option>
                    <option value="textarea">Long text</option>
                    <option value="number">Number</option>
                    <option value="select">Select (choose one)</option>
                    <option value="checkbox">Checkboxes (choose many)</option>
                    <option value="scale">Scale 0-10</option>
                    <option value="boolean">Yes / No</option>
                    <option value="date">Date</option>
                  </select>
                  {(fld.type === "select" || fld.type === "checkbox") ? (
                    <input className="fl-input col-span-2 text-xs" placeholder="opt1, opt2, opt3"
                      value={(fld.options || []).join(", ")}
                      onChange={(e) => updField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                  ) : (
                    <span className="col-span-2" />
                  )}
                  <label className="col-span-1 text-[10px] text-zinc-400 flex items-center gap-1" title="Required"><input type="checkbox" checked={!!fld.required} onChange={(e) => updField(i, { required: e.target.checked })} /> Req</label>
                  <button type="button" onClick={() => rmField(i)} className="col-span-1 text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <button className="fl-btn" data-testid="form-save">Save form</button>
        </form>
      )}

      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: "var(--fl-border)" }}>
        {[["library", `Library (${forms.length})`], ["assigned", `Assigned (${assignments.filter((a) => a.status !== "completed").length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cforms-tab-${k}`}
            className={`px-4 py-3 text-sm uppercase tracking-wider font-semibold ${tab === k ? "text-[#f47a20] border-b-2 border-[#f47a20] -mb-px" : "text-zinc-500 hover:text-zinc-300"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "library" && (
      <div className="grid md:grid-cols-2 gap-4">
        {forms.filter((f) => f.kind !== "ankle_finder").map((f) => (
          <div key={f.id} className="fl-card fl-card-elevated p-5" data-testid={`form-card-${f.id}`}>
            <div className="flex justify-between">
              <div>
                <div className="fl-label text-[#f47a20] flex items-center gap-2 flex-wrap">
                  <span>{f.category} · {f.kind}</span>
                  {f.kind === "pdf" && f.digital_ready && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                      style={{
                        background: f.digital_reviewed
                          ? "rgba(34,197,94,0.14)"
                          : "rgba(255,69,0,0.14)",
                        color: f.digital_reviewed ? "#22c55e" : "#f47a20",
                        border: `1px solid ${f.digital_reviewed ? "rgba(34,197,94,0.35)" : "rgba(255,69,0,0.35)"}`,
                      }}
                      data-testid={`form-digital-status-${f.id}`}
                    >
                      <Sparkles size={9} />
                      {f.digital_reviewed ? "Digital · reviewed" : "Digital · needs review"}
                    </span>
                  )}
                  {f.kind === "pdf" && !f.digital_ready && f.parse_error && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                      style={{ background: "rgba(245,158,11,0.14)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.35)" }}
                      title={f.parse_error}
                      data-testid={`form-parse-warn-${f.id}`}
                    >
                      <AlertCircle size={9} /> Parse failed
                    </span>
                  )}
                </div>
                <div className="fl-heading text-lg mt-1 flex items-center gap-2">
                  {f.kind === "pdf" ? <FileText size={16} className="text-[#f47a20]" /> : <ClipboardEdit size={16} className="text-[#f47a20]" />}
                  {f.title}
                </div>
                {f.description && <div className="text-xs text-zinc-400 mt-1">{f.description}</div>}
                {f.kind === "structured" && <div className="text-xs text-zinc-500 mt-1">{f.fields?.length} fields</div>}
                {f.kind === "pdf" && f.digital_ready && (
                  <div className="text-xs text-zinc-500 mt-1">{(f.parsed_fields || f.fields || []).length} fields detected by AI</div>
                )}
              </div>
              <div className="text-[10px] text-zinc-500">{f.created_by_name}</div>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              <button className="fl-btn text-xs" onClick={() => setAssign(f)} data-testid={`form-assign-${f.id}`}><Send size={12} /> Assign</button>
              {f.kind === "pdf" && (f.digital_ready || (f.parsed_fields || []).length > 0) && (
                <button
                  className="fl-btn-ghost text-xs"
                  onClick={() => openReview(f)}
                  data-testid={`form-review-${f.id}`}
                  title="Review the AI-digitalised version before assigning"
                >
                  <Wand2 size={12} /> Review digital version
                </button>
              )}
              {f.kind === "pdf" && (
                <button onClick={() => openFormPdf(f.id)} className="fl-btn-ghost text-xs" data-testid={`preview-pdf-${f.id}`}><ExternalLink size={12} /> Preview PDF</button>
              )}
              <button
                onClick={() => removeForm(f)}
                disabled={deleting === f.id}
                className="fl-btn-ghost text-xs text-red-400 hover:text-red-300 ml-auto"
                title="Delete form"
                aria-label={`Delete form ${f.title}`}
                data-testid={`form-delete-${f.id}`}
              >
                <Trash2 size={12} /> {deleting === f.id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {tab === "assigned" && (
      <div className="fl-card overflow-hidden" data-testid="assigned-list">
        {assignments.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500 flex flex-col items-center gap-2">
            <ClipboardList size={20} className="text-zinc-600" />
            No form assignments yet. Assign a form from the Library tab.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fl-border)" }}>
            {assignments.map((a) => {
              const done = a.status === "completed";
              return (
                <div key={a.id} className="p-4 flex items-start justify-between gap-4" data-testid={`assignment-row-${a.id}`} style={{ borderColor: "var(--fl-border)" }}>
                  <button
                    onClick={() => done && openResponse(a)}
                    disabled={!done}
                    className={`min-w-0 flex-1 text-left ${done ? "cursor-pointer" : "cursor-default"}`}
                    data-testid={`assignment-open-${a.id}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="fl-heading text-base truncate">{a.form_title}</div>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${done ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                        {done ? <Check size={10} /> : <Clock size={10} />} {done ? "Completed" : "Pending"}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      {a.patient_name || "Unknown patient"}{a.patient_email ? ` · ${a.patient_email}` : ""}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      Assigned by {a.clinician_name || "—"} · {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                      {a.notes ? ` · "${a.notes}"` : ""}
                    </div>
                  </button>
                  <div className="shrink-0 flex flex-col gap-2 items-end">
                    {done && (
                      <button
                        onClick={() => openResponse(a)}
                        className="fl-btn text-xs"
                        data-testid={`assignment-view-${a.id}`}
                      >
                        <Eye size={12} /> View response
                      </button>
                    )}
                    <button
                      onClick={() => removeAssignment(a)}
                      disabled={done || removing === a.id}
                      title={done ? "Completed assignments cannot be removed" : "Remove from patient"}
                      className={`fl-btn-ghost text-xs ${done ? "opacity-40 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                      data-testid={`assignment-remove-${a.id}`}
                    >
                      <Trash2 size={12} /> {removing === a.id ? "Removing…" : "Remove"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}
      {/* Review digital version — appears when clinician clicks "Review digital version" on a PDF card */}
      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-auto" onClick={closeReview} data-testid="review-modal">
          <div
            ref={reviewRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-modal-title"
            className="fl-card fl-card-elevated w-full max-w-3xl max-h-[92vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex items-start justify-between gap-4" style={{ borderColor: "var(--fl-border)" }}>
              <div className="min-w-0">
                <div className="fl-label text-[#f47a20] flex items-center gap-1"><Sparkles size={11} /> AI digitalised</div>
                <div id="review-modal-title" className="fl-heading text-xl mt-1 truncate">{reviewing.title}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Verify each field before assigning to a patient. You can edit labels,
                  change types, add or remove fields, and the original PDF stays on record.
                </div>
              </div>
              <button onClick={() => setReviewing(null)} aria-label="Close review" data-testid="review-close"><X size={16} className="text-zinc-400"/></button>
            </div>

            <div className="p-6 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <div className="fl-label">Fields ({reviewing._draft.length})</div>
                <button type="button" className="fl-btn-ghost text-xs" onClick={rvAdd} data-testid="review-add-field"><Plus size={12} /> Add field</button>
              </div>
              {reviewing._draft.map((fld, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`review-field-${i}`}>
                  <input className="fl-input col-span-2 text-xs" placeholder="key" value={fld.key || ""} onChange={(e) => rvUpdate(i, { key: e.target.value })} />
                  <input className="fl-input col-span-4 text-xs" placeholder="Label" value={fld.label || ""} onChange={(e) => rvUpdate(i, { label: e.target.value })} data-testid={`review-label-${i}`} />
                  <select className="fl-input col-span-2 text-xs" value={fld.type || "text"} onChange={(e) => rvUpdate(i, { type: e.target.value })} data-testid={`review-type-${i}`}>
                    <option value="text">Text</option>
                    <option value="textarea">Long text</option>
                    <option value="number">Number</option>
                    <option value="select">Select (choose one)</option>
                    <option value="checkbox">Checkboxes (choose many)</option>
                    <option value="scale">Scale 0-10</option>
                    <option value="boolean">Yes / No</option>
                    <option value="date">Date</option>
                  </select>
                  {(fld.type === "select" || fld.type === "checkbox") ? (
                    <input className="fl-input col-span-2 text-xs" placeholder="opt1, opt2, opt3"
                      value={(fld.options || []).join(", ")}
                      onChange={(e) => rvUpdate(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                  ) : (
                    <span className="col-span-2" />
                  )}
                  <label className="col-span-1 text-[10px] text-zinc-400 flex items-center gap-1" title="Required"><input type="checkbox" checked={!!fld.required} onChange={(e) => rvUpdate(i, { required: e.target.checked })} /> Req</label>
                  <button type="button" onClick={() => rvRemove(i)} className="col-span-1 text-red-400 flex justify-center" aria-label="Remove field"><Trash2 size={14} /></button>
                </div>
              ))}
              {reviewing._draft.length === 0 && (
                <div className="text-xs text-zinc-500 py-4 text-center">No fields yet — click "Add field" or re-upload the PDF to try parsing again.</div>
              )}
            </div>

            <div className="p-6 border-t flex items-center justify-between gap-3" style={{ borderColor: "var(--fl-border)" }}>
              <button onClick={() => openFormPdf(reviewing.id)} className="text-xs text-zinc-500 hover:text-[color:var(--fl-orange)] inline-flex items-center gap-1">
                <ExternalLink size={11} /> Open original PDF in new tab
              </button>
              <div className="flex items-center gap-2">
                <button onClick={() => setReviewing(null)} className="fl-btn-ghost text-xs" disabled={savingReview} data-testid="review-cancel">Cancel</button>
                <button onClick={saveReview} className="fl-btn text-xs" disabled={savingReview} data-testid="review-save">
                  {savingReview ? <Loader2 size={12} className="animate-spin"/> : <Check size={12} />} Save & mark reviewed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {assign && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={closeAssign}>
          <div
            ref={assignRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-modal-title"
            className="fl-card fl-card-elevated p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between mb-2">
              <div id="assign-modal-title" className="fl-heading text-xl">Assign form</div>
              <button onClick={closeAssign} aria-label="Close"><X className="text-zinc-400" size={16} /></button>
            </div>
            <div className="text-sm text-zinc-400 mb-4">{assign.title}</div>
            {assign.kind === "pdf" && assign.digital_ready && !assign.digital_reviewed && (
              <div
                className="mb-4 p-3 rounded-md text-xs flex items-start gap-2"
                style={{ background: "rgba(255,69,0,0.08)", border: "1px solid rgba(255,69,0,0.35)", color: "var(--fl-muted)" }}
                data-testid="assign-unreviewed-warn"
              >
                <AlertCircle size={14} className="text-[color:var(--fl-orange)] shrink-0 mt-0.5" />
                <div>
                  This form's AI-parsed fields have not been reviewed yet. The patient will see them as-is.
                  Consider opening <b>Review digital version</b> first to verify labels and types.
                </div>
              </div>
            )}
            <div className="fl-label mb-2">Patient</div>
            <select className="fl-input mb-4" value={pickPatient} onChange={(e) => setPickPatient(e.target.value)} data-testid="assign-patient">
              <option value="">— select —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button className="fl-btn w-full justify-center" onClick={doAssign} data-testid="assign-send">Send to patient</button>
          </div>
        </div>
      )}

      {/* Title-picker before PDF upload — replaces window.prompt() (non-accessible & auto-null on cancel) */}
      {uploadPending && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={closeUploadModal}
          data-testid="upload-title-modal"
        >
          <div
            ref={uploadTitleRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="upload-title-title"
            className="fl-card fl-card-elevated p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between mb-3">
              <div id="upload-title-title" className="fl-heading text-lg flex items-center gap-2"><Upload size={14}/> Name this form</div>
              <button onClick={closeUploadModal} aria-label="Cancel upload" disabled={uploading}><X className="text-zinc-400" size={16} /></button>
            </div>
            <p className="text-xs text-zinc-500 mb-3">This is how patients will see it in their Forms tab. You can rename it later.</p>
            <input
              className="fl-input"
              autoFocus
              value={uploadPending.title}
              onChange={(e) => setUploadPending((s) => ({ ...s, title: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter" && uploadPending.title.trim()) uploadPdf(); }}
              placeholder="Form title"
              data-testid="upload-title-input"
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button className="fl-btn-ghost text-sm" onClick={closeUploadModal} disabled={uploading} data-testid="upload-title-cancel">Cancel</button>
              <button className="fl-btn text-sm" onClick={uploadPdf} disabled={uploading || !uploadPending.title.trim()} data-testid="upload-title-submit">
                {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Upload & digitalise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Response viewer — shown when a completed assignment is clicked or when
          a form_submitted notification link brings the clinician here with
          `?assignment=<id>` in the URL. */}
      {viewingResp && viewingResp.response?.kind === "ankle_finder" && (
        <AnkleResponseView responseId={viewingResp.response.ankle_response_id} onClose={() => setViewingResp(null)} />
      )}
      {viewingResp && viewingResp.response?.kind !== "ankle_finder" && (() => {
        const { assignment: a, response: r, patient, form: formDoc } = viewingResp;
        const isPdfResp = r && r.kind === "pdf";
        return (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-3 overflow-auto"
               onClick={() => setViewingResp(null)}>
            <div ref={respDialogRef} role="dialog" aria-modal="true"
                 className="fl-card fl-card-elevated w-full max-w-4xl my-4 max-h-[92vh] overflow-auto"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="response-modal">
              <div className="p-4 md:p-6 border-b flex items-start justify-between gap-4 sticky top-0 z-10"
                   style={{ borderColor: "var(--fl-border)", background: "var(--fl-bg-2)" }}>
                <div className="min-w-0">
                  <div className="fl-label text-[#f47a20]">Patient response</div>
                  <div className="fl-heading text-xl mt-1 truncate">{a.form_title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    From <b>{a.patient_name || patient?.name || "Unknown"}</b>
                    {patient?.email ? ` · ${patient.email}` : ""}
                    {r?.submitted_at ? ` · submitted ${new Date(r.submitted_at).toLocaleString("en-GB")}` : ""}
                  </div>
                </div>
                <button onClick={() => setViewingResp(null)} aria-label="Close" data-testid="response-close">
                  <X size={16} className="text-zinc-400"/>
                </button>
              </div>
              <div className="p-4 md:p-6">
                {respLoading && (
                  <div className="text-sm text-center py-8" style={{ color: "var(--fl-muted-2)" }}>
                    <Loader2 className="animate-spin inline mr-2" size={14}/> Loading response…
                  </div>
                )}
                {!respLoading && !r && (
                  <div className="text-sm text-center py-8" style={{ color: "var(--fl-muted-2)" }}>
                    No response received yet.
                  </div>
                )}
                {!respLoading && r && isPdfResp && (
                  <>
                    <div className="text-xs mb-2" style={{ color: "var(--fl-muted-2)" }}>
                      Annotated PDF returned by the patient. Original template is linked below.
                    </div>
                    <MediaIframe resourceType="form_response" id={r.id}
                            title="annotated-pdf"
                            className="w-full rounded-md border" style={{ height: "68vh", background: "white", borderColor: "var(--fl-border)" }}
                            data-testid="response-pdf-iframe" />
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={async () => { try { const src = await mediaSrc("form_response", r.id); window.open(src, "_blank", "noopener,noreferrer"); } catch { toast.error("Could not open"); } }} className="fl-btn-ghost text-xs" data-testid="response-open-pdf">
                        <ExternalLink size={12}/> Open annotated PDF
                      </button>
                      {formDoc?.kind === "pdf" && (
                        <button onClick={() => openFormPdf(formDoc.id)} className="fl-btn-ghost text-xs" data-testid="response-open-original">
                          <FileText size={12}/> Original template
                        </button>
                      )}
                    </div>
                  </>
                )}
                {!respLoading && r && !isPdfResp && (
                  <>
                    <div className="text-xs mb-3" style={{ color: "var(--fl-muted-2)" }}>Digital form answers</div>
                    <div className="space-y-3">
                      {Object.entries(r.answers || {}).map(([k, v]) => {
                        const fldMeta = (formDoc?.fields || []).find((x) => x.key === k);
                        return (
                          <div key={k} className="fl-card p-3">
                            <div className="fl-label mb-1">{fldMeta?.label || k}</div>
                            <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--fl-text)" }}>
                              {String(v ?? "")}
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(r.answers || {}).length === 0 && (
                        <div className="text-sm text-zinc-500">No answers.</div>
                      )}
                    </div>
                    {formDoc?.kind === "pdf" && (
                      <div className="mt-3">
                        <button onClick={() => openFormPdf(formDoc.id)} className="fl-btn-ghost text-xs" data-testid="response-open-original">
                          <FileText size={12}/> Original template
                        </button>
                      </div>
                    )}
                  </>
                )}
                {!respLoading && r && (
                  <FormSignOffPanel
                    r={r}
                    signName={signName} setSignName={setSignName}
                    signing={signing} onSign={signLock}
                    amendOpen={amendOpen} setAmendOpen={setAmendOpen}
                    amend={amend} setAmend={setAmend}
                    amending={amending} onAmend={addAmendment}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </DashLayout>
  );
}
